import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { NonBreaking } from "@faire/web/common/string";
import { Button } from "@faire/web/slate/Button";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { useSuspenseQuery } from "@tanstack/react-query";
import capitalize from "lodash/capitalize";
import React from "react";

import { getDiscoverPageHeroBannerQueryArgs } from "@faire/retailer/app/_lib/reactQueryPrefetches/getDiscoverPageHeroBannerQueryArgs";
import { ShimmerPlaceHolder } from "@faire/retailer/components/Animation/ShimmerPlaceHolder";
import { getTotalBrandCount } from "@faire/retailer/lib/getMarketingCount";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import { useIsSettingSemSimplifiedLpEnabled } from "@faire/retailer/settings/useSettingSemSimplifiedLp";
import { useSearchPageProductSearchString } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";

import * as styles from "./LoggedOutDiscoverPageHeroBannerPhase3.css";

const DEFAULT_BACKGROUND_COLOR = "#083114";

const DiscoverPageHeroTextPhase3: React.FC<{
  textColor?: string;
  title?: string;
  subtitle?: string;
}> = ({ textColor, title, subtitle }) => {
  const productSearchString = capitalize(useSearchPageProductSearchString());
  const textColorOrDefault: string = textColor ?? Core.text.inverse;
  const isSettingSemSimplifiedLpEnabled = useIsSettingSemSimplifiedLpEnabled();
  const totalBrandCount = getTotalBrandCount();

  const renderTitle = () => {
    if (title) {
      return title;
    }

    return (
      <StrictLocalMsg
        defaultMessage="Wholesale {queryProductName} for your{nbsp}store"
        values={{
          queryProductName: productSearchString,
          nbsp: NonBreaking.space,
        }}
        description={{
          text: "header for discover page explaining you can buy the item at wholesale prices",
          img: "https://cdn.faire.com/fastly/7ab0bf0cdb8db14e66933ffe35b46db73faa9f353d04a80d140b134967137191.png",
        }}
      />
    );
  };

  const renderSubtitle = () => {
    if (isSettingSemSimplifiedLpEnabled) {
      return (
        <StrictLocalMsg
          defaultMessage="Buy from {totalBrandCountNumber}+ brands, all from one place."
          values={{ totalBrandCountNumber: totalBrandCount }}
          description={{
            text: "subheading for discover page, encouraging retailers to browse the site",
          }}
        />
      );
    }

    if (subtitle) {
      return subtitle;
    }

    return (
      <StrictLocalMsg
        defaultMessage="From frames to decorative objects and everything in between, whatever you're looking for, find it on{nbsp}Faire."
        values={{ nbsp: NonBreaking.space }}
        description={{
          text: "subheading for discover page, encouraging retailers to browse the site",
          img: "https://cdn.faire.com/fastly/7ab0bf0cdb8db14e66933ffe35b46db73faa9f353d04a80d140",
        }}
      />
    );
  };

  return (
    <div className={styles.contentWrapper}>
      <Typography
        variant={{
          mobileAndAbove: "pageHeaderSerifRegular",
          tabletAndAbove: "displaySSerifRegular",
          xLargeAndAbove: "displayMSerifRegular",
        }}
        as="h1"
        color={textColorOrDefault}
      >
        {renderTitle()}
      </Typography>
      <Typography
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          xLargeAndAbove: "subheadingSansRegular",
        }}
        color={textColorOrDefault}
      >
        {renderSubtitle()}
      </Typography>
    </div>
  );
};

export const DiscoverPageImageryBannerPhase3: React.FC = () => {
  const onSignUpClick = () => {
    popSignUp(SignUpType.GENERIC, () => {}, {
      element: SignUpElement.TOP_BANNER,
      sourceId: "logged_out_discover_redesign_p3_banner_experiment",
    });
  };

  const isSSR = useIsSSR();

  const searchTerm = useSearchPageProductSearchString();

  const { data: heroBannerData } = useSuspenseQuery({
    ...getDiscoverPageHeroBannerQueryArgs(searchTerm),
    refetchOnMount: false,
  });

  const imgSrc = useResponsiveValue({
    mobileAndAbove: optimizeImageUrl(
      heroBannerData?.background_image ?? {
        url: "https://cdn.faire.com/fastly/47f5406bc2522e2b276072f64a9b376bb7013332c5cbfb185a5771a3023b691e.png",
      },
      isSSR,
      {
        width: 454,
        height: 214,
        resizeMethod: "fit",
      }
    ),
    tabletAndAbove: optimizeImageUrl(
      heroBannerData?.background_image ?? {
        url: "https://cdn.faire.com/fastly/a7468f8f0ee3aad7493bd75b1b09916462094747d1e88490d8bed10cc9b84d51.png",
      },
      isSSR,
      { width: 700, height: 325, resizeMethod: "fit" }
    ),
    desktopAndAbove: optimizeImageUrl(
      heroBannerData?.background_image ?? {
        url: "https://cdn.faire.com/fastly/a7468f8f0ee3aad7493bd75b1b09916462094747d1e88490d8bed10cc9b84d51.png",
      },
      isSSR,
      { width: 800, height: 372, resizeMethod: "fit" }
    ),
    xxLargeDesktop: optimizeImageUrl(
      heroBannerData?.background_image ?? {
        url: "https://cdn.faire.com/fastly/a7468f8f0ee3aad7493bd75b1b09916462094747d1e88490d8bed10cc9b84d51.png",
      },

      isSSR,
      { width: 1200, height: 558, resizeMethod: "fit" }
    ),
  });

  return (
    <div
      className={styles.wrapper}
      style={{
        backgroundColor:
          heroBannerData?.background_color ?? DEFAULT_BACKGROUND_COLOR,
      }}
    >
      <div className={styles.bannerTextWrapper}>
        <DiscoverPageHeroTextPhase3
          textColor={heroBannerData?.text_color}
          title={heroBannerData?.title}
          subtitle={heroBannerData?.subtitle}
        />
        <Button
          onClick={onSignUpClick}
          variant="secondary"
          style={{ border: "none" }}
        >
          <StrictLocalMsg
            defaultMessage="Sign up for free"
            description={{
              text: "sign up cta for discover page header",
              img: "https://cdn.faire.com/fastly/d21596fe32412c4ed5e66b8126825c2cec443186715ab55c7eda5f5488718062.png",
            }}
          />
        </Button>
      </div>
      <div className={styles.bannerImageWrapper}>
        <img
          className={styles.bannerImage}
          src={imgSrc}
          data-test-id="banner-image"
          alt={heroBannerData?.background_image?.alt_text ?? searchTerm}
        />
      </div>
    </div>
  );
};

export const VerticalizedBannerLoadingSkeleton: React.FC = () => {
  const titleHeight = useResponsiveValue({
    mobileAndAbove: 32,
    tabletAndAbove: 76,
    desktopAndAbove: 38,
    xLargeAndAbove: 50,
  });

  const titleWidth = useResponsiveValue({
    mobileAndAbove: 330,
    desktopAndAbove: 356,
    xLargeAndAbove: 510,
  });

  return (
    <div className={styles.loadingWrapper}>
      <div className={styles.bannerTextWrapper}>
        <ShimmerPlaceHolder
          $height={titleHeight}
          $width={titleWidth}
          $backgroundColor={Core.surface.inverse}
        />
        <ShimmerPlaceHolder
          $height={26}
          $width={titleWidth}
          $backgroundColor={Core.surface.inverse}
        />
        <ShimmerPlaceHolder
          $height={71}
          $width={150}
          $backgroundColor={Core.surface.inverse}
        />
      </div>
      <div className={styles.loadingImgSkeleton} />
    </div>
  );
};
